import { currencyFormatter } from '@/helpers/currencyFormatter';
import { Box, Button, Card, Column, Table, useBreakpoint, useOpenClose } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { adderMap, AdderType, ConstructionAdderType, ProgramType, UserPermissions } from 'types';

import { useGetConstructionAddersQuery } from '../../../services/pricing';
import RequirePalmettoFinanceUser from '../../auth/RequirePalmettoFinanceUser';
import RequirePermissions from '../../auth/requirePermissions';
import { EditAdderModal } from '../Adders/EditAdderModal';

import { createCapSchedulePath, supportsCapSchedules } from './capSchedules';
import { CapSchedulesSummary } from './CapSchedulesSummary';
import { CreatePriceCapModal } from './CreatePriceCapModal';

const headerButtonSize = { base: 'sm', desktop: 'md' } as const;

export const PriceCap = () => {
  const { isPhone } = useBreakpoint();
  const navigate = useNavigate();
  const { energyBackupCapSchedules: isCapSchedulesEnabled = false } = useFlags();
  const [selected, setSelected] = useState<any>(null);
  const { programType = ProgramType.energyBackup, priceCapType } = useParams<{
    programType: string;
    priceCapType: string;
  }>();
  const {
    data = [],
    isLoading,
    isFetching,
  } = useGetConstructionAddersQuery({ type: priceCapType as string, programType }, { skip: !priceCapType });
  const { isOpen: isEditModalOpen, handleOpen: openEditModal, handleClose: closeEditModal } = useOpenClose();
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const handleClick = useCallback(
    (row: any) => {
      setSelected(row);
      openEditModal();
    },
    [openEditModal],
  );

  const showCapScheduleButton = isCapSchedulesEnabled && supportsCapSchedules(programType, priceCapType);
  const handleCreateCapSchedule = useCallback(() => {
    navigate(createCapSchedulePath(programType, priceCapType as string));
  }, [navigate, programType, priceCapType]);

  const columnConfig = useMemo(() => {
    switch (priceCapType) {
      case ConstructionAdderType.arbitrageBatteryPriceCap:
      case ConstructionAdderType.backupBatteryPriceCap:
        return [
          {
            heading: 'Name',
            dataKey: 'name',
            render: (_: any, row: any) => (
              <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => handleClick(row)}>
                {row.name}
              </span>
            ),
          },
          {
            heading: 'Caps',
            render: (_: any, row: any) => (
              <Box>
                {row?.energyBackupCaps?.map((cap: any) => (
                  <Box key={cap.manufacturer} direction="row" justifyContent="space-between" wrap>
                    <Box>{cap.manufacturer}</Box>
                    <Box alignSelf="flex-end">
                      <strong>Price Cap:</strong> {currencyFormatter.format(cap.maxPrice)}
                    </Box>
                    <Box>
                      <strong>Term Cap:</strong> {cap.maxTermLength} years
                    </Box>
                  </Box>
                ))}
                <CapSchedulesSummary schedules={row?.energyBackupCapSchedules} />
              </Box>
            ),
          },
        ];
      default:
        return [];
    }
  }, [priceCapType, handleClick]);

  return (
    <>
      <CreatePriceCapModal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        adderType={priceCapType as AdderType}
        programType={programType as ProgramType}
      />
      <EditAdderModal
        isModalOpen={isEditModalOpen}
        closeModal={closeEditModal}
        adderId={selected?.id}
        adderName={selected?.name}
      />
      <Card padding="sm">
        <Card.Header>
          <Box direction="row" justifyContent="space-between">
            <Box fontSize="md" fontWeight="medium">
              {adderMap[programType as ProgramType][priceCapType as AdderType]}
            </Box>
            <RequirePalmettoFinanceUser>
              <RequirePermissions permissions={[UserPermissions.pricingAdmin]}>
                <Box direction="row" childGap="sm">
                  <Button
                    as="button"
                    variant="primary"
                    iconPrefix="add"
                    size={{ base: 'sm', desktop: 'md' }}
                    aria-label="create adder"
                    onClick={openModal}
                  >
                    {!isPhone && 'Option'}
                  </Button>
                  {showCapScheduleButton && (
                    <Button
                      as="button"
                      variant="secondary"
                      iconPrefix="add"
                      size={headerButtonSize}
                      aria-label="create cap schedule"
                      onClick={handleCreateCapSchedule}
                    >
                      {!isPhone && 'Cap schedule'}
                    </Button>
                  )}
                </Box>
              </RequirePermissions>
            </RequirePalmettoFinanceUser>
          </Box>
        </Card.Header>
        <Table
          rowKey="id"
          columns={columnConfig as Column[]}
          rows={data}
          isLoading={isLoading || isFetching}
          isScrollable={{ x: true }}
        />
      </Card>
    </>
  );
};
